import React, { useMemo } from "react";
import "./Payment.scss";
import { useTranslation } from "react-i18next";
import nvCookieHelpers from "../../../services/cookieService";
import * as intrfce from '../Interfaces'
import { useLocalization } from "../../../contexts/LocalizationContext";
// Custom web component types are defined elsewhere (likely in ucp-wrapper types)

interface Props {
    billingData: any;
    savePayment: boolean;
    validationErrors: any;
    formBillingAddress: intrfce.iAddressFormData;
}

const Payment: React.FC<Props> = ({ billingData, savePayment, validationErrors, formBillingAddress }) => {
    const { t } = useTranslation();
    const isLoggedIn = nvCookieHelpers.getCookie('isUserLoggedIn');
    const { stateDropdown, locale, postalCodeRequired } = useLocalization();

    const hasAllRequiredPropsForGuestUser = useMemo(() => {
        const firstName = formBillingAddress?.firstName;
        const lastName = formBillingAddress?.lastName;
        const email = formBillingAddress?.email;
        const country = formBillingAddress?.countryValue;
        const postal_code = formBillingAddress?.pincode;
        const address = formBillingAddress?.address;
        const city = formBillingAddress?.cityValue;
        const state = locale === 'zh-tw' ? "TW" : formBillingAddress?.stateValue;

        return firstName?.trim() && lastName?.trim() && email?.trim() && country?.trim() && (postalCodeRequired ? postal_code?.trim() : true) && address?.trim() && city?.trim() && (stateDropdown || locale === 'zh-tw' ? state?.trim() : true);
    }, [formBillingAddress, locale, postalCodeRequired, stateDropdown]);

    const hasAllRequiredPropsForRegisteredUser = useMemo(() => {
        // Prioritize form input over saved billing data, use ?? to properly handle empty strings
        const firstName = formBillingAddress?.firstName ?? billingData?.firstName;
        const lastName = formBillingAddress?.lastName ?? billingData?.lastName;
        // const email = formBillingAddress?.email ?? billingData?.email;
        const country = formBillingAddress?.countryValue ?? billingData?.country;
        const postal_code = formBillingAddress?.pincode ?? billingData?.postalCode;
        const address = formBillingAddress?.address ?? billingData?.address;
        const city = formBillingAddress?.cityValue ?? billingData?.city;
        const state = locale === 'zh-tw' ? "TW" : (formBillingAddress?.stateValue ?? billingData?.state);

        return firstName?.trim() && lastName?.trim() && country?.trim() && (postalCodeRequired ? postal_code?.trim() : true) && address?.trim() && city?.trim() && (stateDropdown || locale === 'zh-tw' ? state?.trim() : true);
    }, [billingData, formBillingAddress, locale, postalCodeRequired, stateDropdown]);
    // Memoize keys to prevent unnecessary re-renders - only changes when actual data changes
    const billingDataKey = useMemo(() => {
        return formBillingAddress ? JSON.stringify(formBillingAddress) : JSON.stringify(billingData);
    }, [billingData, formBillingAddress]);

    const guestDataKey = useMemo(() => {
        return JSON.stringify(formBillingAddress);
    }, [formBillingAddress]);

    return (
        <div className="payment mb-5">
            <h3 className={`PaymentHeader`}>{t('PAYMENT_INFORMATION')}</h3>

            {validationErrors.paymentSrc && <p className="text-danger" style={{ color: '#dc3545', margin: '10px 0' }}>{validationErrors.paymentSrc}</p>}
            {
                isLoggedIn ? (
                    hasAllRequiredPropsForRegisteredUser ? (
                        <>
                            <registered-user-payment-methods-v2
                                key={billingDataKey}
                                {...({
                                    name: `${billingData?.firstName || formBillingAddress?.firstName} ${billingData?.lastName || formBillingAddress?.lastName}`,
                                    country: formBillingAddress?.countryValue || billingData?.country || '',
                                    postal_code: postalCodeRequired ? (formBillingAddress?.pincode ?? billingData?.postalCode ?? '') : '',
                                    address_line_1: formBillingAddress?.address ?? billingData?.address ?? '',
                                    address_line_2: formBillingAddress?.line2 ?? billingData?.line2 ?? '',
                                    city: formBillingAddress?.cityValue ?? billingData?.city ?? '',
                                    state: locale === 'zh-tw' ? "TW" : (formBillingAddress?.stateValue ?? billingData?.state ?? ''),
                                    full_address: true,
                                } as any)}
                                // payment-config="nvstore-standard-payments"
                            />
                        </>
                    ) : (
                            <RequiredFieldsAlert message={t('COMPLETE_THE_REQUIRED_FIELDS')} />
                    )
                ) : (
                        hasAllRequiredPropsForGuestUser ? (
                            <guest-payment-method-v2
                                key={guestDataKey}
                                {...({
                                    name: `${formBillingAddress?.firstName || ''} ${formBillingAddress?.lastName || ''}`,
                                    email: formBillingAddress?.email || '',
                                    country: formBillingAddress?.countryValue || '',
                                    postal_code: postalCodeRequired ? (formBillingAddress?.pincode || '') : '',
                                    save_payment: savePayment,
                                    address_line_1: formBillingAddress?.address,
                                    address_line_2: formBillingAddress?.line2,
                                    city: formBillingAddress?.cityValue,
                                    state: formBillingAddress?.stateValue,
                                    full_address: true,
                                } as any)}
                                // payment-config="nvstore-standard-payments"
                            />
                        ) : (
                            <RequiredFieldsAlert message={t('COMPLETE_THE_BILLING_INFORMATION')} />
                        )
                )
            }

        </div >
    )
};

Payment.displayName = 'Payment';

// Custom comparison function to prevent unnecessary rerenders
// Only rerender if actual data changes, not reference changes
const arePropsEqual = (prevProps: Props, nextProps: Props) => {
    // Compare primitive values
    if (prevProps.savePayment !== nextProps.savePayment) return false;

    // Deep compare formBillingAddress
    const prevBillingAddress = prevProps.formBillingAddress;
    const nextBillingAddress = nextProps.formBillingAddress;
    if (JSON.stringify(prevBillingAddress) !== JSON.stringify(nextBillingAddress)) return false;
    
    // Deep compare billingData (selectedBillingAddress)
    if (JSON.stringify(prevProps.billingData) !== JSON.stringify(nextProps.billingData)) return false;
    
    // Deep compare validationErrors
    if (JSON.stringify(prevProps.validationErrors) !== JSON.stringify(nextProps.validationErrors)) return false;
    
    return true;
};

// Export with custom comparison
export default React.memo(Payment, arePropsEqual);

// Convert to proper React component to avoid creating new elements on every render
const RequiredFieldsAlert: React.FC<{ message: string }> = React.memo(({ message }) => {
    return (
        <div
            className="payment-required-fields-alert"
            role="alert"
            aria-live="polite"
        >
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M12 2L2 20h20L12 2zm0 3.5L19.53 19H4.47L12 5.5zM11 10v4h2v-4h-2zm0 5v2h2v-2h-2z"
                    fill="#B86E00"
                />
            </svg>
            <span className="payment-required-fields-message">
                {message}
            </span>
        </div>
    );
});

RequiredFieldsAlert.displayName = 'RequiredFieldsAlert';